import { Controller }    from "@hotwired/stimulus"
import { Calendar }      from "@fullcalendar/core"
import dayGridPlugin     from "@fullcalendar/daygrid"
import interactionPlugin from "@fullcalendar/interaction"

const OPEN    = "sky-cal-day--open"
const FULL    = "sky-cal-day--full"
const CLOSED  = "sky-cal-day--closed"
const CHOSEN  = "sky-cal-day--selected"

export default class extends Controller {
  static targets = ["calendar", "dateInput", "field", "fieldLabel", "panel", "popover", "message"]

  static values = {
    url:             String,
    packageOptionId: String,
    selectedDate:    String,
  }

  calendar = null

  connect() {
    // dateStr -> { available_slots, total_slots, operational_start, operational_end }
    this._availability = new Map()
    // range key -> Promise, so prev/next navigation doesn't refetch a month
    this._cache = new Map()
    // [from, to] ISO pairs we have answers for — lets us tell "closed" apart
    // from "not fetched yet", which absence from _availability alone can't
    this._loadedRanges = []
    this._today = this._iso(new Date())

    this._open = false

    this._initCalendar()
    this._renderField()
  }

  disconnect() {
    this.calendar?.destroy()
    this.calendar = null
  }

  // ── popover ────────────────────────────────────────────────────────────────

  toggle() {
    this._open ? this.close() : this.open()
  }

  open() {
    if (this._open) return

    this._open = true
    this.panelTarget.classList.remove("hidden")
    this.fieldTarget.setAttribute("aria-expanded", "true")

    // FullCalendar measures at render time, and it rendered while the panel was
    // display:none — so it computed a zero-width grid. Re-measure now that the
    // panel is laid out, or the month comes out collapsed.
    this.calendar?.updateSize()
  }

  close() {
    if (!this._open) return

    this._open = false
    this.panelTarget.classList.add("hidden")
    this.fieldTarget.setAttribute("aria-expanded", "false")
  }

  // wired as click@window — the field's own click->toggle runs first, and the
  // field is inside popoverTarget, so opening doesn't immediately close
  closeOnOutsideClick(event) {
    if (!this._open) return
    if (this.popoverTarget.contains(event.target)) return

    this.close()
  }

  closeOnEscape() {
    if (!this._open) return

    this.close()
    this.fieldTarget.focus()
  }

  _initCalendar() {
    this.calendar = new Calendar(this.calendarTarget, {
      plugins:             [dayGridPlugin, interactionPlugin],
      initialView:         "dayGridMonth",
      initialDate:         this.selectedDateValue || undefined,
      firstDay:            1,
      height:              "auto",
      fixedWeekCount:      false,
      showNonCurrentDates: false,
      selectable:          false,
      // month granularity, not `today` — clamping to the exact day would clip
      // the current month's grid and leave blank leading weeks. Past days
      // inside this month are handled per-cell in _paint / _onDateClick.
      validRange:          { start: this._today.slice(0, 8) + "01" },
      headerToolbar:       { left: "prev", center: "title", right: "next" },

      datesSet:  (info) => this._beginLoad(info.start, info.end),
      dateClick: (info) => this._onDateClick(info.dateStr),
    })

    this.calendar.render()
  }

  // ── availability ───────────────────────────────────────────────────────────

  // Both callers fire this and walk away, so a rejection anywhere past the fetch
  // becomes an unhandled promise the console can only pin on the call site.
  // Catch it here, where there is still enough context to say something useful.
  _beginLoad(start, end) {
    this._loadRange(start, end).catch((error) => {
      // We cancel our own requests when the package option changes; that is a
      // normal outcome, not a failure worth reporting.
      if (error?.name === "AbortError") return

      console.error("[booking-calendar] availability load failed", error)
      this._showMessage("Could not load availability. Please try again.")
    })
  }

  async _loadRange(start, end) {
    const from = this._iso(start)
    const to   = this._iso(new Date(end.getTime() - 86400000)) // `end` is exclusive
    const key  = `${this.packageOptionIdValue}|${from}|${to}`

    if (!this._cache.has(key)) {
      this._cache.set(key, this._fetchRange(from, to))
    }

    // paint whatever we already know so the grid isn't blank while fetching
    this._paint()

    const token = this._cache
    let dates

    try {
      dates = await this._cache.get(key)
    } catch {
      this._cache.delete(key)
      this._showMessage("Could not load availability. Please try again.")
      return
    }

    // a package-option change swapped the cache out from under us — that
    // request's data belongs to the previous drone point, so drop it
    if (token !== this._cache) return

    dates.forEach((entry) => this._availability.set(entry.date, entry))
    this._loadedRanges.push([from, to])
    this._paint()
    this._reconcileSelection()
  }

  async _fetchRange(from, to) {
    const url = new URL(this.urlValue, window.location.origin)
    url.searchParams.set("package_option_id", this.packageOptionIdValue)
    url.searchParams.set("start", from)
    url.searchParams.set("end", to)

    const response = await fetch(url.toString(), { headers: { Accept: "application/json" } })
    if (!response.ok) throw new Error(`available_dates responded ${response.status}`)

    return response.json()
  }

  // ── painting ───────────────────────────────────────────────────────────────

  _paint() {
    this.calendarTarget.querySelectorAll(".fc-daygrid-day[data-date]").forEach((cell) => {
      cell.classList.remove(OPEN, FULL, CLOSED, CHOSEN)

      // days spilling in from an adjacent month render blank
      if (cell.classList.contains("fc-day-other")) return

      const dateStr = cell.dataset.date

      // a past day may well still have a schedule row — never offer it
      if (dateStr < this._today) {
        cell.classList.add(CLOSED)
        cell.title = "This date has passed"
        return
      }

      const entry = this._availability.get(dateStr)

      if (!entry) {
        cell.classList.add(CLOSED)
        cell.title = "Closed — no flights scheduled"
        return
      }

      if (entry.available_slots > 0) {
        cell.classList.add(OPEN)
        // the count moved out of the cell (the dot is the indicator) — keep it
        // on hover, where it costs no space
        const slots = entry.available_slots === 1 ? "1 slot" : `${entry.available_slots} slots`
        cell.title = `${slots} available · ${entry.operational_start}–${entry.operational_end}`
      } else {
        cell.classList.add(FULL)
        cell.title = "Fully booked"
      }

      if (dateStr === this.selectedDateValue) cell.classList.add(CHOSEN)
    })
  }

  // ── selection ──────────────────────────────────────────────────────────────

  _onDateClick(dateStr) {
    if (dateStr < this._today) {
      this._showMessage("That date has already passed. Please pick an upcoming day.")
      return
    }

    const entry = this._availability.get(dateStr)

    if (!entry) {
      this._showMessage("We don't fly on that date. Please pick a highlighted day.")
      return
    }
    if (entry.available_slots === 0) {
      this._showMessage("That date is fully booked. Please pick another day.")
      return
    }

    this._select(dateStr)
  }

  _select(dateStr) {
    this.selectedDateValue     = dateStr
    this.dateInputTarget.value = dateStr

    // hidden inputs never fire `change` on their own — this is what hands off
    // to checkout-schedule#fetchOptions and reloads the schedule-options frame
    this.dateInputTarget.dispatchEvent(new Event("change", { bubbles: true }))

    this._clearMessage()
    this._renderField()
    this._paint()
    this.close()
  }

  // Drops the chosen date when this drone point can't actually serve it —
  // either because the package option changed, or because set_ivars seeded
  // today's date on a day the point doesn't fly.
  _reconcileSelection() {
    const chosen = this.selectedDateValue
    if (!chosen) return

    if (chosen < this._today) {
      this._clearSelection()
      this._showMessage("That date has already passed. Please pick an upcoming day.")
      return
    }

    // the date may simply sit outside the months we've fetched so far
    if (!this._isLoaded(chosen)) return

    const entry = this._availability.get(chosen)
    if (entry && entry.available_slots > 0) return

    this._clearSelection()
    this._showMessage(
      entry
        ? "That date is fully booked. Please pick another day."
        : "That date isn't available at this location. Please pick another day."
    )
  }

  // ISO dates compare correctly as strings
  _isLoaded(dateStr) {
    return this._loadedRanges.some(([from, to]) => dateStr >= from && dateStr <= to)
  }

  _clearSelection() {
    this.selectedDateValue     = ""
    this.dateInputTarget.value = ""

    this._renderField()
    this._clearSlots()
    this._paint()
  }

  // Empties the schedule-options frame and locks Continue. Mirrors
  // checkout-schedule#disableSubmit — no turbo:frame-load fires when we blank
  // the frame ourselves, so that controller won't do it for us.
  _clearSlots() {
    const button = document.getElementById("checkoutSubmitButton")
    if (button) button.disabled = true

    const frame = document.getElementById("schedule-options")
    if (!frame) return

    // checkout-schedule#connect seeds this frame with today's date before we
    // know whether the point even flies today. Dropping `src` mid-flight makes
    // Turbo cancel the request (frame_controller#sourceURLChanged), and the
    // aborted fetch surfaces as an unhandled rejection — while the response we
    // raced would have repainted the slots we just emptied anyway. Turbo marks
    // the frame [busy] for the duration, so let the load land and clear after.
    if (frame.hasAttribute("busy")) {
      frame.addEventListener("turbo:frame-load", () => this._clearSlots(), { once: true })
      return
    }

    frame.removeAttribute("src")
    frame.innerHTML = ""
  }

  // ── package option switching ───────────────────────────────────────────────

  dronePointChanged(event) {
    const packageOptionId = event.detail?.packageOptionId
    if (packageOptionId) this.packageOptionIdValue = packageOptionId

    // availability belongs to the old drone point — start over. Replacing the
    // Map (rather than clearing it) invalidates any in-flight request.
    this._availability = new Map()
    this._cache        = new Map()
    this._loadedRanges = []

    this._clearMessage()
    this._paint()

    const view = this.calendar.view
    this._beginLoad(view.activeStart, view.activeEnd)
  }

  // ── small helpers ──────────────────────────────────────────────────────────

  _renderField() {
    if (!this.hasFieldLabelTarget) return

    if (!this.selectedDateValue) {
      this.fieldLabelTarget.textContent = "Select a date"
      this.fieldLabelTarget.classList.add("text-muted-foreground")
      this.fieldLabelTarget.classList.remove("font-medium", "text-navy")
      return
    }

    const [year, month, day] = this.selectedDateValue.split("-").map(Number)
    this.fieldLabelTarget.textContent = new Intl.DateTimeFormat("en-GB", {
      weekday: "long", day: "numeric", month: "long", year: "numeric",
    }).format(new Date(year, month - 1, day))

    this.fieldLabelTarget.classList.remove("text-muted-foreground")
    this.fieldLabelTarget.classList.add("font-medium", "text-navy")
  }

  _showMessage(text) {
    if (!this.hasMessageTarget) return
    this.messageTarget.textContent = text
    this.messageTarget.classList.remove("hidden")
  }

  _clearMessage() {
    if (!this.hasMessageTarget) return
    this.messageTarget.textContent = ""
    this.messageTarget.classList.add("hidden")
  }

  // Local-time YYYY-MM-DD. toISOString() would shift the date across the
  // UTC boundary for anyone east or west of Greenwich.
  _iso(date) {
    const month = String(date.getMonth() + 1).padStart(2, "0")
    const day   = String(date.getDate()).padStart(2, "0")
    return `${date.getFullYear()}-${month}-${day}`
  }
}
